import { IS_CLIENT, IS_PRODUCTION, sha256Hash } from '@repo/common-utils';
import { UtmData } from '@repo/platform';
import mixpanel, { Config } from 'mixpanel-browser';

import { MixpanelInitializeProps, MixpanelGateRefreshProps, SendEventProperties, SetUserParams, EventTrackingInitializeProps } from '../types';

import { BaseService } from './BaseService';

// 로그인 여부 세그먼트 프로퍼티 — enableLoginStatusProperty 옵션을 켠 앱만 쌓는다
const LOGIN_STATUS_PROPERTY_KEY = 'login_status';
const LOGIN_STATUS = {
  LOGIN: 'login',
  NON_LOGIN: 'non_login'
} as const;

// Mixpanel 자체의 주소창 자동 캡처(init 시점 타이밍에 좌우됨) 대신, 앱이 이미 확보해 둔 utmData를 그대로 얹기 위한 매핑
const mapUtmToMixpanelAttributes = (utmData?: UtmData): Record<string, string> => {
  if (!utmData) return {};
  return Object.fromEntries(
    Object.entries({
      utm_source: utmData.utmSource,
      utm_medium: utmData.utmMedium,
      utm_campaign: utmData.utmCampaign,
      utm_content: utmData.utmContent,
      utm_term: utmData.utmTerm
    }).filter((entry): entry is [string, string] => typeof entry[1] === 'string')
  );
};

export class MixpanelService extends BaseService {
  private readonly mixpanelKey?: string;
  private mixpanelConfig?: Partial<Config>;
  private readonly landingId?: string;
  private readonly enableLoginStatusProperty: boolean;
  private utmData?: UtmData;
  private isInit: boolean = false;
  private canInitializeMixpanel: boolean = false;
  private preservedEvents: { eventName: string; properties?: SendEventProperties }[] = [];

  constructor({
    appId,
    clientId,
    mixpanelKey,
    mixpanelConfig,
    enableLoginStatusProperty,
    utmData,
    landingId,
    canInitializeMixpanel
  }: EventTrackingInitializeProps & MixpanelInitializeProps) {
    super(appId, clientId);
    this.mixpanelKey = mixpanelKey;
    this.landingId = landingId;
    this.canInitializeMixpanel = canInitializeMixpanel ?? false;
    this.enableLoginStatusProperty = enableLoginStatusProperty ?? false;
    this.utmData = utmData;
    this.mixpanelConfig = {
      debug: !IS_PRODUCTION && process.env.NEXT_PUBLIC_TRACKING_DEBUG_MODE === 'true',
      track_pageview: false,
      persistence: 'cookie',
      cross_subdomain_cookie: true,
      ...mixpanelConfig
    };
    this.init();
  }

  protected init() {
    if (!IS_CLIENT || !this.mixpanelKey) return;
    if (!this.canInitializeMixpanel) return;

    mixpanel.init(this.mixpanelKey, this.mixpanelConfig);
    const attributes = {
      ...this.commonAttributes,
      ...(this.landingId ? { landing_id: this.landingId } : { landing_id: null }),
      // 주소창의 utm 파라미터가 이미 정리된 뒤에 초기화되더라도 유실되지 않도록, 앱이 미리 캡처해 둔 값을 직접 등록한다
      ...mapUtmToMixpanelAttributes(this.utmData)
    };
    mixpanel.register(attributes);
    if (this.enableLoginStatusProperty) {
      // 재방문 로그인 유저의 쿠키에 남은 login 값을 덮지 않도록 register_once로 기본값만 채운다
      mixpanel.register_once({ [LOGIN_STATUS_PROPERTY_KEY]: LOGIN_STATUS.NON_LOGIN });
    }
    this.isInit = true;

    this.preservedEvents.forEach(({ eventName, properties }) => this.sendEvent(eventName, properties));
    this.preservedEvents = [];
  }

  get mixpanelDeviceId(): string {
    if (this.isInit) {
      return mixpanel.get_property('$device_id');
    }
    return '';
  }

  setCanInitializeMixpanel(canInitializeMixpanel: boolean, refreshed?: MixpanelGateRefreshProps) {
    this.canInitializeMixpanel = canInitializeMixpanel;
    // 게이트가 열리는(canInitializeMixpanel=true) 시점에 최신 값으로 갱신해야 init()에 정확한 값이 반영된다
    if (refreshed?.utmData) this.utmData = refreshed.utmData;
    if (refreshed?.mixpanelConfig) this.mixpanelConfig = { ...this.mixpanelConfig, ...refreshed.mixpanelConfig };
    if (this.canInitializeMixpanel && !this.isInit) {
      this.init();
    }
  }

  async setUser({ id = '', name = '', phone = '', email = '' }: SetUserParams) {
    try {
      if (this.isInit) {
        mixpanel.identify(id);
        if (this.enableLoginStatusProperty) {
          mixpanel.register({ [LOGIN_STATUS_PROPERTY_KEY]: LOGIN_STATUS.LOGIN });
          mixpanel.people.set({ [LOGIN_STATUS_PROPERTY_KEY]: LOGIN_STATUS.LOGIN });
        }
        mixpanel.people.union({
          user_id: id,
          name_hash: await sha256Hash(name || ''),
          phone_hash: await sha256Hash(phone || ''),
          email_hash: await sha256Hash(email || '')
        });
      }
    } catch (err) {
      console.info('mixpanel set user error', err);
      throw err;
    }
  }

  sendEvent(eventName: string, properties?: SendEventProperties) {
    if (!this.isInit) {
      this.preservedEvents.push({ eventName, properties });
      return;
    }

    const mergedProperties = {
      ...this.commonAttributes,
      ...(this.landingId && { landing_id: this.landingId }),
      ...properties,
      is_server: false
    };
    mixpanel.track(eventName, mergedProperties, {
      send_immediately: true
    });

    return {
      config: {
        distinct_id: mixpanel.get_distinct_id(),
        ...this.mixpanelConfig
      },
      event: {
        name: eventName,
        ...properties,
        ...this.commonAttributes,
        ...(this.landingId && { landing_id: this.landingId }),
        is_server: false
      }
    };
  }

  unsetUser(): void {
    if (this.enableLoginStatusProperty) {
      // reset 전에 기록해야 식별된 프로필에 남는다
      mixpanel.people.set({ [LOGIN_STATUS_PROPERTY_KEY]: LOGIN_STATUS.NON_LOGIN });
    }
    mixpanel.reset();
    if (this.enableLoginStatusProperty) {
      // reset이 super property를 비우므로 이후 익명 이벤트용으로 재등록한다
      mixpanel.register({ [LOGIN_STATUS_PROPERTY_KEY]: LOGIN_STATUS.NON_LOGIN });
    }
  }
}
